import * as React from "react";
import { Tag, TagGroup } from "@fluentui/react-components";
import { useAppMenuItemCustomStyles } from "./useAppMenuItemStyles";
import type {
  ShortcutMetadata,
  ShortcutPillsProps,
} from "./ShortcutPills.types";

export const renderShortcutPills = (props: ShortcutPillsProps) => {
  return <ShortcutPills {...props} />;
};

export const ShortcutPills = React.memo(
  ({
    secondaryText,
    shortcutMetadata,
    showAllShortcuts = false,
  }: ShortcutPillsProps) => {
    const itemStyles = useAppMenuItemCustomStyles();

    const tokenizedShortcuts = getShortcutTokens(
      secondaryText,
      shortcutMetadata
    );

    if (!tokenizedShortcuts) {
      return secondaryText;
    }

    // Only display the first shortcut if showAllShortcuts is false
    const shortcutsToDisplay = showAllShortcuts
      ? tokenizedShortcuts
      : [tokenizedShortcuts[0]];

    return (
      <>
        {shortcutsToDisplay.map((shortcut, shortcutIndex) => (
          <>
            <TagGroup
              key={shortcutIndex}
              size="extra-small"
              className={itemStyles.shortcutPillsTagGroup}
            >
              {shortcut.map((token, index) => (
                <Pill key={index} token={token} />
              ))}
            </TagGroup>
            {/* Add delimiter between shortcuts */}
            {shortcutIndex < shortcutsToDisplay.length - 1 && (
              <span>{shortcutMetadata?.delimiter}</span>
            )}
          </>
        ))}
      </>
    );
  }
);

const Pill = React.memo((props: { token: string }) => {
  return <Tag>{props.token}</Tag>;
});

/**
 * Split a shortcut string using delimiter and array of separators. If splitting fails, return null.
 * eg: "Ctrl+B or Ctrl+2" => [["Ctrl", "B"], ["Ctrl", "2"]]
 * @param shortcutString   input shortcut string to be tokenized
 * @param shortcutMeta  Contains localized delimiter and separators string required for parsing shortcuts into pills
 */
export const getShortcutTokens = (
  shortcutString?: string,
  shortcutMetadata?: ShortcutMetadata
): string[][] | null => {
  // Handle edge cases
  if (!shortcutString || !shortcutMetadata) {
    return null;
  }

  // combine all separators into a single regex pattern
  const separatorPattern = new RegExp(
    `${shortcutMetadata.separators
      // escape special characters in the separator
      .map((sep) => sep.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
      .join("|")}`,
    "g"
  );

  const shortcuts = shortcutString.split(shortcutMetadata.delimiter);
  return shortcuts.map((shortcut) => shortcut.split(separatorPattern));
};
